import { useState } from "react";
import { toast } from "react-toastify";
import { FiPlus } from "react-icons/fi";
import { getKpiMeasurements, addKpiMeasurement } from "../../services/kraKpiApi";
import useAsync from "../../hooks/useAsync";
import { Card, Button, Modal, FormField, DataTable } from "../common";
import { getErrorMessage } from "../../utils/errors";
import { formatDate, todayISO } from "../../utils/format";

const addDaysISO = (day, n) => {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export function currentMeasurements(list = []) {
  const byPeriod = new Map();
  list.forEach((m) => {
    const key = `${m.periodStart}|${m.periodEnd}`;
    const prev = byPeriod.get(key);
    if (!prev || Number(m.revision) > Number(prev.revision)) byPeriod.set(key, m);
  });
  return [...byPeriod.values()].sort((a, b) => String(a.periodStart).localeCompare(String(b.periodStart)));
}

export const latestMeasurement = (list) => {
  const current = currentMeasurements(list);
  return current.length ? current[current.length - 1] : null;
};

export default function KpiMeasurements({ kpi, canRecord = false, readOnlyNote }) {
  const list = useAsync(() => getKpiMeasurements(kpi.id), [kpi.id]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const latest = latestMeasurement(list.data || []);

  const save = async () => {
    if (form.value === "" || Number(form.value) < 0) { toast.error("Enter the actual value."); return; }
    if (!form.periodStart || !form.periodEnd || form.periodEnd < form.periodStart) { toast.error("The period end must be on or after its start."); return; }
    if (form.periodEnd > todayISO()) { toast.error("The period can't end in the future."); return; }
    const correcting = (list.data || []).some((m) => m.periodStart === form.periodStart && m.periodEnd === form.periodEnd);
    if (correcting && !String(form.reason || "").trim()) { toast.error("This period already has a value. Add a note explaining the correction."); return; }
    setBusy(true);
    try {
      await addKpiMeasurement(kpi.id, form);
      toast.success("Measurement recorded.");
      setForm(null);
      list.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const subtitle = [
    `Target ${kpi.target} ${kpi.unit}.`,
    kpi.frequency ? `${kpi.frequency} measurements.` : "",
    latest ? `Latest recorded: ${latest.value} ${kpi.unit} (${formatDate(latest.periodStart)} to ${formatDate(latest.periodEnd)}).` : "",
  ].filter(Boolean).join(" ");

  return (
    <Card
      title={kpi.name}
      subtitle={subtitle}
      padded={false}
      actions={canRecord && <Button size="sm" variant="secondary" icon={FiPlus} onClick={() => {
        const last = latestMeasurement(list.data || []);
        const firstOfMonth = `${todayISO().slice(0, 8)}01`;
        const afterLast = last ? addDaysISO(last.periodEnd, 1) : null;
        const start = afterLast && afterLast > firstOfMonth && afterLast <= todayISO() ? afterLast : firstOfMonth;
        setForm({ periodStart: start, periodEnd: todayISO(), value: "", reason: "" });
      }}>Record actual</Button>}
    >
      <DataTable keepOrder
        dense
        rows={list.data || []}
        loading={list.loading && !list.data}
        emptyTitle={list.error ? "Could not load measurements" : "No measurements yet"}
        emptyText={list.error || readOnlyNote}
        columns={[
          { key: "period", label: "Period", render: (r) => `${formatDate(r.periodStart)} to ${formatDate(r.periodEnd)}` },
          { key: "value", label: "Actual", align: "right", render: (r) => `${r.value} ${kpi.unit}` },
          { key: "revision", label: "Revision", align: "right" },
          { key: "by", label: "Recorded by" },
        ]}
      />
      {canRecord && (
        <Modal
          open={Boolean(form)}
          size="sm"
          title={`Record ${kpi.name}`}
          onClose={busy ? undefined : () => setForm(null)}
          footer={<><Button variant="ghost" onClick={() => setForm(null)} disabled={busy}>Cancel</Button><Button onClick={save} loading={busy}>Save</Button></>}
        >
          {form && (
            <div className="form-stack">
              <div className="form-grid">
                <FormField label="Period start"><input type="date" value={form.periodStart} onChange={(e) => setForm({ ...form, periodStart: e.target.value })} /></FormField>
                <FormField label="Period end"><input type="date" value={form.periodEnd} max={todayISO()} onChange={(e) => setForm({ ...form, periodEnd: e.target.value })} /></FormField>
              </div>
              <FormField label={`Actual (${kpi.unit})`} required><input type="number" step="any" min="0" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></FormField>
              {(() => {
                const correcting = (list.data || []).some((m) => m.periodStart === form.periodStart && m.periodEnd === form.periodEnd);
                return (
                  <FormField label="Note" required={correcting} hint={correcting ? "This period already has a value: explain the correction." : "Optional. Required when correcting an earlier value for the same period."}>
                    <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
                  </FormField>
                );
              })()}
            </div>
          )}
        </Modal>
      )}
    </Card>
  );
}

export function KpiRecordedHint({ kpi, onUse, disabled }) {
  const list = useAsync(() => getKpiMeasurements(kpi.id), [kpi.id]);
  if (list.loading && !list.data) return null;
  if (list.error) return null;
  const current = currentMeasurements(list.data || []);
  if (!current.length) return <small className="muted kpi-recorded">No measurements recorded during the year.</small>;
  const latest = current[current.length - 1];
  return (
    <small className="kpi-recorded">
      Recorded during the year: <strong>{latest.value} {kpi.unit}</strong> ({formatDate(latest.periodStart)} to {formatDate(latest.periodEnd)})
      {current.length > 1 ? `, ${current.length} periods: ${current.map((m) => m.value).join(", ")}` : ""}.
      {onUse && !disabled && (
        <button type="button" className="link-btn" onClick={() => onUse(latest.value)}>Use {latest.value}</button>
      )}
    </small>
  );
}
